import { useMemo, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import CtaBand from "@/components/feature/CtaBand";
import NewsCard from "@/components/feature/NewsCard";
import { news, newsCategories } from "@/mocks/news";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 5;

export default function News() {
  useDocumentTitle(
    "芒果传媒新闻动态 - 最新行业资讯与公司动态",
    "芒果传媒新闻动态，汇集公司最新项目进展、行业资讯与荣誉奖项，了解芒果传媒的最新动向。"
  );

  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return news
      .filter((n) => category === "全部" || n.category === category)
      .filter((n) => !kw || n.title.toLowerCase().includes(kw) || n.summary.toLowerCase().includes(kw))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [keyword, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetAll = () => {
    setKeyword("");
    setCategory("全部");
    setPage(1);
  };

  return (
    <div>
      <PageHero
        title="新闻动态"
        subtitle="在这里了解芒果传媒的最新项目进展、行业观察与公司动态。"
        image="https://readdy.ai/api/search-image?query=Modern%20editorial%20workspace%20with%20newspapers%20and%20screens%20showing%20media%20content%2C%20warm%20daylight%2C%20professional%20atmosphere%2C%20no%20text&width=1920&height=700&seq=mango-news-hero&orientation=landscape"
        crumbs={[{ label: "新闻动态" }]}
      />

      <section className="bg-background-50 py-10 md:py-14">
        <div className="container-site">
          <div className="bg-secondary-50 rounded-lg p-5 md:p-6 border border-background-200/80">
            <div className="flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 text-lg"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  placeholder="搜索新闻关键词…"
                  className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {newsCategories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      setCategory(c);
                      setPage(1);
                    }}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      category === c
                        ? "bg-primary-500 text-foreground-950"
                        : "bg-background-50 text-foreground-700 hover:bg-primary-50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="text-sm text-foreground-600 mt-6">
            共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 条新闻
          </p>

          {pageItems.length > 0 ? (
            <div className="flex flex-col gap-5 mt-6">
              {pageItems.map((n) => (
                <NewsCard key={n.id} item={n} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-search-eye-line text-3xl text-secondary-600"></i>
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-950 mt-5">
                没有找到相关新闻
              </h3>
              <p className="text-sm text-foreground-600 mt-2">
                试着换一个关键词或分类，也可以直接查看全部新闻。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-foreground-950 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置搜索
              </button>
            </div>
          )}

          {totalPages > 1 && pageItems.length > 0 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                className="px-4 py-2 rounded-md border border-background-200 text-sm font-medium text-foreground-700 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                上一页
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i + 1)}
                  className={`w-10 h-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    currentPage === i + 1
                      ? "bg-primary-500 text-foreground-950"
                      : "border border-background-200 text-foreground-700 hover:bg-secondary-50"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                className="px-4 py-2 rounded-md border border-background-200 text-sm font-medium text-foreground-700 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </div>
  );
}